import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { publicApi, fileUrl } from "../lib/apiClient";
import CanvasGrid from "../components/CanvasGrid";
import Walkthrough3D from "../components/Walkthrough3D";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { LAYERS, totalArea } from "../lib/blueprint";
import { Ruler, Eye, EyeOff, Box, Loader2, ArrowRight, MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";

export default function PublicView() {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [activeLayers, setActiveLayers] = useState({ structural: true, electrical: true, plumbing: true, hvac: true, engineering: true, furniture: true });
  const [waOpen, setWaOpen] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentMode, setCommentMode] = useState(false);
  const [pending, setPending] = useState(null); // {x,y}
  const [author, setAuthor] = useState("");
  const [text, setText] = useState("");
  const [floorIdx, setFloorIdx] = useState(0);

  const loadComments = useCallback(() => {
    publicApi.get(`/public/projects/${shareId}/comments`).then((r) => setComments(r.data)).catch(() => {});
  }, [shareId]);

  useEffect(() => {
    publicApi.get(`/public/projects/${shareId}`).then((r) => setData(r.data)).catch(() => setError(true));
    loadComments();
  }, [shareId, loadComments]);

  const toggle = (l) => setActiveLayers((s) => ({ ...s, [l]: !s[l] }));

  const onAddComment = (x, y) => { setPending({ x, y }); };

  const submitComment = async () => {
    if (!text.trim()) { toast.error("Write a comment"); return; }
    try {
      await publicApi.post(`/public/projects/${shareId}/comments`, { x: pending.x, y: pending.y, author: author || "Guest", text });
      toast.success("Comment added");
      setPending(null); setText("");
      loadComments();
    } catch { toast.error("Could not post comment"); }
  };

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#0D1117] text-[#F0F6FC]">
        <p className="text-[#8B949E]">This shared blueprint is unavailable.</p>
        <Link to="/" className="mt-4"><Button className="bg-[#38BDF8] text-[#0D1117] font-semibold">Go to ArchiMesh AI</Button></Link>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0D1117]"><Loader2 className="w-8 h-8 animate-spin text-[#38BDF8]" /></div>;
  }

  const floors = data.blueprint?.floors;
  const _f = (floors && floors[floorIdx]) || null;
  const bp = _f
    ? { rooms: _f.rooms || [], items: _f.items || [], walls: _f.walls || [] }
    : { rooms: data.blueprint?.rooms || [], items: data.blueprint?.items || [], walls: data.blueprint?.walls || [] };

  return (
    <div className="h-screen flex flex-col bg-[#0D1117] text-[#F0F6FC] overflow-hidden">
      <header className="h-14 shrink-0 glass border-b border-[#30363D] flex items-center px-4 gap-3 z-30">
        <Link to="/" className="flex items-center gap-2">
          <Ruler className="w-5 h-5 text-[#38BDF8]" />
          <span className="font-heading font-bold">ArchiMesh<span className="text-[#38BDF8]">AI</span></span>
        </Link>
        <div className="h-6 w-px bg-[#30363D]" />
        <div>
          <div className="font-heading font-semibold text-sm leading-tight">{data.name}</div>
          <div className="text-[10px] text-[#8B949E] font-mono-cad uppercase tracking-widest">Read-only · {data.city}</div>
        </div>
        <div className="flex-1" />
        <Button size="sm" variant="outline" onClick={() => setCommentMode((v) => !v)} data-testid="public-comment-toggle"
          className={`border-[#30363D] bg-transparent ${commentMode ? "text-[#38BDF8] border-[#38BDF8]" : "hover:text-[#38BDF8] hover:border-[#38BDF8]"}`}>
          <MessageSquarePlus className="w-4 h-4 mr-1.5" /> {commentMode ? "Click plan to pin" : `Comment (${comments.length})`}
        </Button>
        <Button size="sm" variant="outline" onClick={() => setWaOpen(true)} data-testid="public-3d-btn" className="border-[#30363D] bg-transparent hover:bg-[#A855F7]/10 hover:text-[#A855F7] hover:border-[#A855F7]">
          <Box className="w-4 h-4 mr-1.5" /> 3D View
        </Button>
        <Link to={`/bundle/${shareId}`}>
          <Button size="sm" variant="outline" className="border-[#30363D] bg-transparent hover:bg-[#34D399]/10 hover:text-[#34D399] hover:border-[#34D399]" data-testid="public-bundle-btn">
            Buyer Bundle
          </Button>
        </Link>
        <Link to="/"><Button size="sm" className="bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0D1117] font-semibold">Make your own <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
      </header>

      <div className="flex-1 relative overflow-hidden">
        <CanvasGrid blueprint={bp} setBlueprint={() => {}} tool="select" activeLayers={activeLayers}
          selectedId={null} setSelectedId={() => {}} pxPerFt={13} readOnly
          comments={comments} commentMode={commentMode} onAddComment={onAddComment} />

        <div className="absolute top-4 left-4 glass rounded-xl border border-[#30363D] p-2 space-y-1">
          <div className="px-2 pb-1 text-[10px] uppercase tracking-widest text-[#6E7681] font-mono-cad">Layers</div>
          {Object.entries(LAYERS).map(([l, cfg]) => (
            <button key={l} onClick={() => toggle(l)} data-testid={`public-layer-${l}`}
              className="flex items-center gap-2 w-full px-2 py-1 rounded-md hover:bg-[#21262D] transition-colors text-sm">
              {activeLayers[l] ? <Eye className="w-3.5 h-3.5" style={{ color: cfg.accent }} /> : <EyeOff className="w-3.5 h-3.5 text-[#6E7681]" />}
              <span style={{ color: activeLayers[l] ? cfg.accent : "#6E7681" }}>{cfg.label}</span>
            </button>
          ))}
        </div>

        {floors && floors.length > 1 && (
          <div className="absolute bottom-4 left-4 glass rounded-xl border border-[#30363D] p-1.5 flex gap-1" data-testid="public-floor-switch">
            {floors.map((f, i) => (
              <button key={i} onClick={() => setFloorIdx(i)} data-testid={`public-floor-${i}`}
                className={`px-3 py-1.5 rounded-md text-xs font-mono-cad transition-colors ${i === floorIdx ? "bg-[#38BDF8] text-[#0D1117] font-semibold" : "text-[#8B949E] hover:bg-[#21262D]"}`}>
                {f.name || `Floor ${i + 1}`}
              </button>
            ))}
          </div>
        )}

        {data.render_path && (
          <img src={fileUrl(data.render_path)} alt="render" className="absolute top-4 right-4 w-52 h-32 object-cover rounded-lg border border-[#30363D]" />
        )}
        {data.video_path && (
          <video src={fileUrl(data.video_path)} controls loop muted playsInline
            className="absolute right-4 w-52 rounded-lg border border-[#30363D] bg-black" style={{ top: data.render_path ? "156px" : "16px" }} data-testid="public-video" />
        )}

        <div className="absolute bottom-4 right-4 glass rounded-lg border border-[#30363D] px-4 py-2 font-mono-cad text-xs text-[#8B949E]">
          <span className="text-[#38BDF8]">{totalArea(bp.rooms)}</span> ft² · {bp.rooms.length} rooms · {comments.length} comments
        </div>
      </div>

      <Walkthrough3D open={waOpen} onOpenChange={setWaOpen} blueprint={bp} floors={floors} roofStyle={data.blueprint?.roof_style || "Gable"} site={data.blueprint?.site} />

      <Dialog open={!!pending} onOpenChange={(v) => { if (!v) setPending(null); }}>
        <DialogContent className="max-w-md bg-[#161B22] border-[#30363D] text-[#F0F6FC]">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">Leave a comment</DialogTitle>
            <DialogDescription className="text-[#8B949E]">Pin feedback at this spot on the blueprint.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input value={author} onChange={(e) => setAuthor(e.target.value)} placeholder="Your name" className="bg-[#0D1117] border-[#30363D]" data-testid="comment-author-input" />
            <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Your feedback…" className="bg-[#0D1117] border-[#30363D] min-h-[80px]" data-testid="comment-text-input" />
            <Button onClick={submitComment} className="w-full bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0D1117] font-semibold" data-testid="comment-submit-btn">Post Comment</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
